import { Injectable, signal, WritableSignal } from "@angular/core";

/**
 * How far (px) a gesture has to travel against the current direction before it counts as a
 * reversal. Finger jitter and the last sample of a flick routinely move a pixel or two the wrong
 * way, which would otherwise flip the Views filter and restart its animation mid-scroll.
 */
const REVERSAL_DISTANCE_PX = 8;

@Injectable({
  providedIn: "root",
})
export class ScrollDirectionService {
  // Signal to track scroll direction: 'up', 'down', or null
  scrollDirection: WritableSignal<"up" | "down" | null> = signal<"up" | "down" | null>(null);

  // Distance travelled against the current direction since it last agreed with the gesture.
  private reversedDistance = 0;

  /**
   * Records the direction of a wheel/touch gesture over the page scroll container.
   * Gestures are ignored while a scroll lock holds the page still (e.g. search dropdown open),
   * and "down" is ignored when the page is too short to scroll — otherwise the Views filter
   * would hide even though nothing moved.
   *
   * `distance` is how far this event moved. The first direction is taken immediately; after that
   * it only flips once the movement against it adds up to REVERSAL_DISTANCE_PX. The default is
   * enough to flip on its own, for callers (wheel notches) that have no jitter to filter out.
   */
  report(
    direction: "up" | "down",
    container: HTMLElement | null | undefined,
    distance: number = REVERSAL_DISTANCE_PX,
  ): void {
    if (container?.hasAttribute("data-scroll-locked")) {
      return;
    }
    if (direction === "down" && container && container.scrollHeight <= container.clientHeight) {
      return;
    }

    const current = this.scrollDirection();
    if (direction === current) {
      this.reversedDistance = 0;
      return;
    }
    if (current !== null) {
      this.reversedDistance += distance;
      if (this.reversedDistance < REVERSAL_DISTANCE_PX) {
        return;
      }
    }
    this.reversedDistance = 0;
    this.scrollDirection.set(direction);
  }
}
